import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Loader2, Phone, MessageSquare, UserRound, Check } from "lucide-react";
import { toast } from "sonner";
import { ONBOARDING_COPY, WHATSAPP_OPTIN_COPY, maskPhone } from "@shared/notificationCopy";
import { EgyptianPhoneInput } from "../ui/EgyptianPhoneInput";
import {
  initiatePhoneAuth,
  sendPhoneOtp,
  verifyPhoneAuth,
  getPhoneAuthStatus,
  updateUserProfile,
  type AuthResponse,
} from "../../api/authService";
import {
  normalizeEgyptianPhoneNumber,
  formatEgyptianPhoneNumber,
} from "../../utils/phoneUtils";
import { usePostHogTracking } from "../../hooks/usePostHogTracking";
import WhatsappOptInCheckbox from "../notifications/WhatsappOptInCheckbox";
import { useAuth } from "../../contexts/AuthContext";
import { grantWhatsappConsent } from "../../api/notificationService";
import { recordWhatsappOnboardingDecline } from "../../api/onboardingService";
import {
  FINISH_WHATSAPP_INITIAL,
  buildFinishConsentRequest,
  decideFinishSubmit,
} from "../../lib/onboardingUi";

// "finish" (Phase 49 UI-SPEC S2) replaced the old name-only step: every new phone account lands
// on it, returning accounts never do.
type PhoneAuthStep = "phone" | "otp" | "finish" | "complete";

const finishCopy = ONBOARDING_COPY.finish;

// The masked number sits in its own dir="ltr" span inside the RTL sentence (same SLOT split as
// WhatsappOptInCheckbox).
const SLOT = "\u0000";
function AlreadyOnLine({ phoneMasked }: { phoneMasked: string }) {
  const [before, after = ""] = WHATSAPP_OPTIN_COPY.onboardingAlreadyOn(SLOT).split(SLOT);
  return (
    <>
      {before}
      <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
        {phoneMasked}
      </span>
      {after}
    </>
  );
}

interface PhoneAuthProps {
  onSuccess?: (authResponse: AuthResponse) => void;
  onCancel?: () => void;
}

export const PhoneAuth: React.FC<PhoneAuthProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { trackAuth, trackNotifications, trackOnboarding } = usePostHogTracking();
  const { getSignupContext } = useAuth();
  const [step, setStep] = useState<PhoneAuthStep>("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [fullName, setFullName] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts, setMaxAttempts] = useState(5);
  const [authResponse, setAuthResponse] = useState<AuthResponse | null>(null);
  // D-16: explicit WhatsApp consent, unchecked by default.
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  // S2 finish-screen switch. Separate from the OTP box and always starts OFF (T-49-02): consent is
  // only recorded when the user turns it on and submits.
  const [finishWhatsappOn, setFinishWhatsappOn] = useState(FINISH_WHATSAPP_INITIAL);
  // The OTP box was ticked when verify succeeded: consent (source login_otp) already exists, so
  // the finish screen shows the confirmation line and records nothing again (D-13).
  const [whatsappAlreadyOn, setWhatsappAlreadyOn] = useState(false);
  const [nameError, setNameError] = useState("");

  // Countdown timer for resend button
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      setError("من فضلك أدخل رقم الهاتف");
      return;
    }

    // Normalize the Egyptian phone number
    const normalizeResult = normalizeEgyptianPhoneNumber(phoneNumber);

    if (!normalizeResult.success) {
      setError(normalizeResult.error || "تنسيق رقم الهاتف غير صحيح");
      return;
    }

    const normalizedPhone = normalizeResult.phoneNumber!;

    setLoading(true);
    setError("");
    trackAuth.phoneAuthInitiated();

    try {
      // Step 1: Create transaction
      const initiateResult = await initiatePhoneAuth({
        phoneNumber: normalizedPhone,
      });

      if (!initiateResult.success) {
        setError(initiateResult.error?.message || "فشل في إنشاء طلب المصادقة");
        setLoading(false);
        return;
      }

      if (!initiateResult.data?.transactionId) {
        setError("لم يتم الحصول على معرف المعاملة");
        setLoading(false);
        return;
      }

      const txId = initiateResult.data.transactionId;
      setTransactionId(txId);

      // Step 2: Send OTP
      const otpResult = await sendPhoneOtp({ transactionId: txId });

      if (!otpResult.success) {
        setError(otpResult.error?.message || "فشل في إرسال رمز التحقق");
        setLoading(false);
        return;
      }

      if (otpResult.data?.verificationId) {
        setVerificationId(otpResult.data.verificationId);
      }

      trackAuth.phoneOtpSent();
      setSuccess(`تم إرسال رمز التحقق إلى `);
      setStep("otp");
      setCountdown(60); // 60 seconds before allowing resend
    } catch (error) {
      console.error("Phone auth error:", error);
      setError("حدث خطأ غير متوقع");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError("من فضلك أدخل رمز التحقق");
      return;
    }

    if (otp.length < 4) {
      setError("رمز التحقق يجب أن يكون 4 أرقام على الأقل");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const verifyResult = await verifyPhoneAuth({
        transactionId,
        otp,
        whatsappOptIn,
        // D-04: the trigger and store that opened the auth modal, stamped only on a new account.
        signupContext: getSignupContext(),
      });

      if (!verifyResult.success) {
        setError(verifyResult.error?.message || "رمز التحقق غير صحيح");

        // Update attempts count
        const statusResult = await getPhoneAuthStatus(transactionId);
        if (statusResult.success && statusResult.data) {
          setAttempts(statusResult.data.attempts);
          setMaxAttempts(statusResult.data.maxAttempts);
        }

        setLoading(false);
        return;
      }

      if (!verifyResult.data) {
        setError("فشل في تسجيل الدخول");
        setLoading(false);
        return;
      }

      trackAuth.phoneVerified({ is_new_user: verifyResult.data.isNewUser });
      // Only after a successful verify, and never with the number in props.
      if (whatsappOptIn) {
        trackNotifications.whatsappOptinChanged({ enabled: true, source: "login_otp" });
      }

      // Store auth response for potential name collection step
      setAuthResponse(verifyResult.data);

      // Store token immediately so updateUserProfile can access it
      const authData = {
        user: verifyResult.data.user,
        token: verifyResult.data.token,
        isNewUser: verifyResult.data.isNewUser,
      };
      localStorage.setItem("yetfarrag_auth", JSON.stringify(authData));

      // Every new account finishes on S2 (name + WhatsApp); returning accounts go straight in.
      if (verifyResult.data.isNewUser) {
        setWhatsappAlreadyOn(whatsappOptIn);
        setFullName(verifyResult.data.user.full_name || "");
        setSuccess(finishCopy.verifiedAlert);
        setStep("finish");
      } else {
        setSuccess("تم تسجيل الدخول بنجاح!");
        setStep("complete");

        if (onSuccess) {
          onSuccess(verifyResult.data);
        }
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setError("حدث خطأ غير متوقع");
    } finally {
      setLoading(false);
    }
  };

  const handleFinishSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = fullName.trim();
    if (!name) {
      setNameError(finishCopy.nameRequired);
      return;
    }
    if (name.length < 2) {
      setNameError(finishCopy.nameTooShort);
      return;
    }

    setLoading(true);
    setNameError("");
    setError("");

    try {
      // Uses the `yetfarrag_auth` blob written right after verify.
      const updateResult = await updateUserProfile({ fullName: name });
      if (!updateResult.success) {
        setError(finishCopy.nameSaveFailed);
        return;
      }

      // Consent or decline only AFTER the name saved. The decision lives in the tested helper.
      switch (decideFinishSubmit({ finishWhatsappOn, whatsappAlreadyOn })) {
        case "grant": {
          // Option by reference: the server resolves the verified login number, no phone is sent.
          const consent = await grantWhatsappConsent(buildFinishConsentRequest());
          if (consent.ok) {
            trackNotifications.whatsappOptinChanged({ enabled: true, source: "onboarding" });
          } else {
            // Never blocks: the name is saved, the user can turn WhatsApp on from settings.
            toast.error(finishCopy.consentFailedToast);
          }
          break;
        }
        case "decline":
          // D-23: stamps the decline marker (hides the bell card 8 days, enables the one second ask).
          void recordWhatsappOnboardingDecline();
          break;
        case "none":
          // Already on from the OTP box: the stored login_otp consent is never re-recorded.
          break;
      }

      trackOnboarding.signupFinishSubmitted({ whatsappOptin: finishWhatsappOn, whatsappAlreadyOn });

      if (authResponse) {
        const updatedAuthResponse = {
          ...authResponse,
          user: { ...authResponse.user, full_name: name },
        };
        setAuthResponse(updatedAuthResponse);
        setSuccess("");
        setStep("complete");
        if (onSuccess) {
          onSuccess(updatedAuthResponse);
        }
      }
    } catch (error) {
      console.error("Finish account error:", error);
      setError(finishCopy.nameSaveFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;

    setLoading(true);
    setError("");

    try {
      const otpResult = await sendPhoneOtp({ transactionId });

      if (!otpResult.success) {
        setError(otpResult.error?.message || "فشل في إعادة إرسال رمز التحقق");
        setLoading(false);
        return;
      }

      if (otpResult.data?.verificationId) {
        setVerificationId(otpResult.data.verificationId);
      }

      trackAuth.phoneOtpResent();
      setSuccess("تم إعادة إرسال رمز التحقق");
      setCountdown(60);
      setOtp(""); // Clear previous OTP
    } catch (error) {
      console.error("Resend OTP error:", error);
      setError("حدث خطأ غير متوقع");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === "otp") {
      setStep("phone");
      setOtp("");
      setError("");
      setSuccess("");
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        {step === "finish" ? (
          <CardTitle className="flex items-center gap-2" style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>
            <UserRound className="h-5 w-5" />
            {finishCopy.title}
          </CardTitle>
        ) : (
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            تسجيل الدخول برقم الهاتف
          </CardTitle>
        )}
        <CardDescription>
          {step === "phone" && "أدخل رقم هاتفك لتلقي رمز التحقق"}
          {step === "otp" && "أدخل رمز التحقق المرسل إلى هاتفك"}
          {step === "finish" && finishCopy.description}
          {step === "complete" && "تم تسجيل الدخول بنجاح"}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>
              {success}
              {step === "phone" && (
                <span className="inline-block" dir="ltr">
                  {phoneNumber && `${formatEgyptianPhoneNumber(phoneNumber)}`}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {step === "phone" && (
          <form onSubmit={handlePhoneSubmit} className="space-y-4">
            <EgyptianPhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              disabled={loading}
              placeholder="01234567890"
            />

            <div className="flex gap-2">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    جارٍ الإرسال...
                  </>
                ) : (
                  <>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    إرسال رمز التحقق
                  </>
                )}
              </Button>

              {onCancel && (
                <Button type="button" variant="outline" onClick={handleCancel}>
                  إلغاء
                </Button>
              )}
            </div>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">رمز التحقق</Label>
              <Input
                id="otp"
                type="text"
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} // Only allow digits
                maxLength={6}
                dir="ltr"
                className="text-center text-lg tracking-widest"
                disabled={loading}
                autoFocus
              />
              {attempts > 0 && (
                <p className="text-xs text-muted-foreground">
                  المحاولة {attempts} من {maxAttempts}
                </p>
              )}
            </div>

            <WhatsappOptInCheckbox
              variant="login"
              checked={whatsappOptIn}
              onCheckedChange={setWhatsappOptIn}
              disabled={loading}
            />

            <div className="flex gap-2">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    جارٍ التحقق...
                  </>
                ) : (
                  "تأكيد"
                )}
              </Button>

              <Button type="button" variant="outline" onClick={handleBack}>
                رجوع
              </Button>
            </div>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                onClick={handleResendOtp}
                disabled={countdown > 0 || loading}
                className="text-sm"
              >
                {countdown > 0
                  ? `إعادة الإرسال بعد ${countdown} ثانية`
                  : "إعادة إرسال الرمز"}
              </Button>
            </div>
          </form>
        )}

        {step === "finish" && (
          <form onSubmit={handleFinishSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="fullName">{finishCopy.nameLabel}</Label>
              <Input
                id="fullName"
                type="text"
                placeholder={finishCopy.namePlaceholder}
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  if (nameError) setNameError("");
                }}
                disabled={loading}
                autoFocus
                required
                aria-invalid={nameError ? true : undefined}
                aria-describedby={nameError ? "fullName-error" : "fullName-helper"}
              />
              {nameError ? (
                <p
                  id="fullName-error"
                  role="alert"
                  style={{ fontSize: 12, fontWeight: 400, lineHeight: 1.5, color: "var(--ys-danger)", margin: 0 }}
                >
                  {nameError}
                </p>
              ) : (
                <p id="fullName-helper" className="text-xs text-muted-foreground">
                  {finishCopy.nameHelper}
                </p>
              )}
            </div>

            {whatsappAlreadyOn ? (
              <p
                dir="rtl"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: "var(--ys-ink-2)",
                  margin: "16px 0 0",
                }}
              >
                <Check aria-hidden="true" size={16} style={{ color: "var(--ys-success)", flexShrink: 0 }} />
                <span>
                  <AlreadyOnLine phoneMasked={maskPhone(phoneNumber)} />
                </span>
              </p>
            ) : (
              <div style={{ marginTop: 16 }}>
                <WhatsappOptInCheckbox
                  variant="onboarding"
                  checked={finishWhatsappOn}
                  onCheckedChange={setFinishWhatsappOn}
                  phoneMasked={maskPhone(phoneNumber)}
                  disabled={loading}
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !fullName.trim()}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  {finishCopy.submitting}
                </>
              ) : (
                finishCopy.submit
              )}
            </Button>
          </form>
        )}

        {step === "complete" && (
          <div className="text-center space-y-4">
            <div className="text-green-600 text-lg font-medium">
              ✅ تم تسجيل الدخول بنجاح
            </div>
            <Button onClick={() => window.location.reload()} className="w-full">
              متابعة
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
